import { useState, useMemo, useEffect } from "react";
import {
  useQuery,
  createPlaybook,
  updatePlaybook,
  getTradingStrategiesByUser,
  getAccountsByUser,
  getAccountGroupsByUser,
  getPropFirmRulesForAccount,
} from "wasp/client/operations";
import type { Playbook } from "wasp/entities";
import { Plus, Trash2, Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { TimePicker } from "../client/components/ui/time-picker";
import { Label } from "../client/components/ui/label";
import { Textarea } from "../client/components/ui/textarea";
import { Badge } from "../client/components/ui/badge";
import { Switch } from "../client/components/ui/switch";
import { Progress } from "../client/components/ui/progress";
import { useToast } from "../client/hooks/use-toast";
import { cn } from "../client/utils";
import {
  COMMON_MARKETS,
  SESSION_PRESETS,
  PLAYBOOK_STEP_COUNT,
  type PlaybookSession,
  type PlaybookSetup,
  type PlaybookRisk,
  type PlaybookPropFirmOverrides,
} from "./constants";
import TargetSelector from "./TargetSelector";

type PlaybookFormProps = {
  /** Existing active version to edit. Omit to create the first version. */
  playbook?: Playbook | null;
  /** Renders without the outer page chrome, for the Edgy side drawer. */
  embedded?: boolean;
  onSaved?: (playbook: Playbook) => void;
  onCancel?: () => void;
};

type FormState = {
  strategyId: string | null;
  strategyName: string;
  name: string;
  markets: string[];
  sessions: PlaybookSession[];
  setups: PlaybookSetup[];
  maxRiskPerTrade: PlaybookRisk | null;
  maxTradesPerDay: number | null;
  minRiskReward: number | null;
  sourceAccountId: string | null;
  targetAccountIds: string[];
  targetGroupIds: string[];
  propFirmRuleOverrides: PlaybookPropFirmOverrides | null;
  personalRules: string;
};

function initialState(playbook?: Playbook | null): FormState {
  if (!playbook) {
    return {
      strategyId: null,
      name: "",
      strategyName: "",
      markets: [],
      sessions: [],
      setups: [{ name: "", entryRules: "", exitRules: "" }],
      maxRiskPerTrade: { type: "percent", value: 1 },
      maxTradesPerDay: null,
      minRiskReward: null,
      sourceAccountId: null,
      targetAccountIds: [],
      targetGroupIds: [],
      propFirmRuleOverrides: null,
      personalRules: "",
    };
  }

  return {
    strategyId: playbook.strategyId,
    // Les playbooks d'avant la migration n'ont pas de nom propre.
    name: playbook.name ?? playbook.strategyName,
    strategyName: playbook.strategyName,
    markets: playbook.markets ?? [],
    sessions: (playbook.sessions as PlaybookSession[]) ?? [],
    setups: (playbook.setups as PlaybookSetup[]) ?? [],
    maxRiskPerTrade: (playbook.maxRiskPerTrade as PlaybookRisk | null) ?? null,
    maxTradesPerDay: playbook.maxTradesPerDay,
    minRiskReward: playbook.minRiskReward,
    sourceAccountId: playbook.sourceAccountId,
    targetAccountIds: (playbook as any).targetAccounts?.map((a: any) => a.id) ?? [],
    targetGroupIds: (playbook as any).targetGroups?.map((g: any) => g.id) ?? [],
    propFirmRuleOverrides: (playbook.propFirmRuleOverrides as PlaybookPropFirmOverrides | null) ?? null,
    personalRules: playbook.personalRules ?? "",
  };
}

export default function PlaybookForm({ playbook, embedded = false, onSaved, onCancel }: PlaybookFormProps) {
  const t = useTranslation();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(() => initialState(playbook));
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [marketInput, setMarketInput] = useState("");

  const { data: strategies } = useQuery(getTradingStrategiesByUser);
  const { data: accountsData } = useQuery(getAccountsByUser);
  // getAccountsByUser returns { accounts, hiddenAccountsCount } — not an array.
  // Memoised so the fallback [] keeps a stable identity across renders.
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData]);
  const { data: groupsData } = useQuery(getAccountGroupsByUser);
  const groups = useMemo(() => groupsData ?? [], [groupsData]);

  // Prop firm rules are always re-derived from the account, never stored (option B):
  // the prop firm stays the source of truth, only overrides are persisted.
  const { data: derivedRules } = useQuery(
    getPropFirmRulesForAccount,
    { accountId: form.sourceAccountId ?? "" },
    { enabled: !!form.sourceAccountId }
  );

  const isEditing = !!playbook;
  const hasOverrides = form.propFirmRuleOverrides !== null;

  // Pré-sélectionne le compte le plus récent, comme compte de référence et comme
  // première cible d'analyse, pour que l'étape 3 soit utilisable sans rien cocher.
  useEffect(() => {
    if (!form.sourceAccountId && accounts.length > 0 && !isEditing) {
      setForm((f) => ({
        ...f,
        sourceAccountId: accounts[0].id,
        targetAccountIds: f.targetAccountIds.length === 0 ? [accounts[0].id] : f.targetAccountIds,
      }));
    }
  }, [accounts, form.sourceAccountId, isEditing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  function validateStep(current: number): string[] {
    const found: string[] = [];

    if (current === 1) {
      if (!form.name.trim()) found.push(t.playbook.nameRequired);
      if (!form.strategyName.trim()) found.push(t.playbook.validation.strategyNameRequired);
      if (form.markets.length === 0) found.push(t.playbook.validation.marketsRequired);
    }

    if (current === 2) {
      const named = form.setups.filter((s) => s.name.trim().length > 0);
      if (named.length === 0) found.push(t.playbook.validation.setupRequired);
      if (form.setups.some((s) => !s.name.trim() && (s.entryRules.trim() || s.exitRules.trim()))) {
        found.push(t.playbook.validation.setupNameRequired);
      }
      if (form.maxRiskPerTrade && form.maxRiskPerTrade.value <= 0) {
        found.push(t.playbook.validation.riskValueRequired);
      }
    }

    return found;
  }

  function goNext() {
    const found = validateStep(step);
    setErrors(found);
    if (found.length === 0) setStep((s) => Math.min(s + 1, PLAYBOOK_STEP_COUNT));
  }

  function goBack() {
    setErrors([]);
    if (step === 1) onCancel?.();
    else setStep((s) => Math.max(s - 1, 1));
  }

  async function handleSave() {
    const allErrors = [...validateStep(1), ...validateStep(2)];
    setErrors(allErrors);
    if (allErrors.length > 0) {
      setStep(validateStep(1).length > 0 ? 1 : 2);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        strategyId: form.strategyId,
        name: form.name.trim(),
        strategyName: form.strategyName.trim(),
        markets: form.markets,
        sessions: form.sessions,
        // Drop the empty rows the form starts with.
        setups: form.setups.filter((s) => s.name.trim().length > 0),
        maxRiskPerTrade: form.maxRiskPerTrade,
        maxTradesPerDay: form.maxTradesPerDay,
        minRiskReward: form.minRiskReward,
        sourceAccountId: form.sourceAccountId,
        targetAccountIds: form.targetAccountIds,
        targetGroupIds: form.targetGroupIds,
        propFirmRuleOverrides: form.propFirmRuleOverrides,
        personalRules: form.personalRules.trim() || null,
      };

      const saved = playbook
        ? await updatePlaybook({ ...payload, id: playbook.id })
        : await createPlaybook(payload);

      toast({
        description: playbook ? t.playbook.newVersionToast : t.playbook.savedToast,
      });
      onSaved?.(saved);
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.message || t.playbook.saveError,
      });
    } finally {
      setIsSaving(false);
    }
  }

  function toggleMarket(market: string) {
    update(
      "markets",
      form.markets.includes(market)
        ? form.markets.filter((m) => m !== market)
        : [...form.markets, market]
    );
  }

  function addCustomMarket() {
    const value = marketInput.trim().toUpperCase();
    if (value && !form.markets.includes(value)) {
      update("markets", [...form.markets, value]);
    }
    setMarketInput("");
  }

  const stepLabels = [t.playbook.steps.strategy, t.playbook.steps.setups, t.playbook.steps.rules];
  const progress = (step / PLAYBOOK_STEP_COUNT) * 100;

  const selectedAccount = useMemo(
    () => accounts.find((a: any) => a.id === form.sourceAccountId) ?? null,
    [accounts, form.sourceAccountId]
  );

  const effectiveRules = hasOverrides ? form.propFirmRuleOverrides : derivedRules?.derived ?? null;

  return (
    <div className={cn("flex flex-col gap-6", embedded ? "p-1" : "max-w-3xl mx-auto")}>
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold font-display">{t.playbook.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t.playbook.subtitle}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{stepLabels[step - 1]}</span>
          <span className="text-muted-foreground">
            {t.playbook.stepOf
              .replace("{current}", String(step))
              .replace("{total}", String(PLAYBOOK_STEP_COUNT))}
          </span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {errors.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <ul className="list-disc list-inside space-y-0.5">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">{t.playbook.strategyStep.description}</p>

          <div className="flex flex-col gap-2">
            <Label htmlFor="playbook-name">{t.playbook.strategyStep.strategyNameLabel}</Label>
            <Input
              id="playbook-name"
              value={form.strategyName}
              placeholder={t.playbook.strategyStep.strategyNamePlaceholder}
              onChange={(e) => update("strategyName", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t.playbook.strategyStep.strategyNameHint}</p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="playbook-free-name">{t.playbook.nameLabel}</Label>
            <Input
              id="playbook-free-name"
              value={form.name}
              placeholder={t.playbook.namePlaceholder}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="playbook-strategy">{t.playbook.strategyStep.strategyLabel}</Label>
            <select
              id="playbook-strategy"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.strategyId ?? ""}
              // Immutable identity: the strategy is fixed once the first version exists.
              disabled={isEditing}
              onChange={(e) => update("strategyId", e.target.value || null)}
            >
              <option value="">{t.playbook.strategyStep.strategyPlaceholder}</option>
              {strategies?.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t.playbook.strategyStep.marketsLabel}</Label>
            <p className="text-xs text-muted-foreground">{t.playbook.strategyStep.marketsHint}</p>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_MARKETS.map((market) => (
                <Badge
                  key={market}
                  variant={form.markets.includes(market) ? "default" : "outline"}
                  className="cursor-pointer font-mono"
                  onClick={() => toggleMarket(market)}
                >
                  {market}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2 mt-1">
              <Input
                value={marketInput}
                onChange={(e) => setMarketInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomMarket();
                  }
                }}
                className="h-9 font-mono"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCustomMarket}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {form.markets.filter((m) => !COMMON_MARKETS.includes(m as any)).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {form.markets
                  .filter((m) => !COMMON_MARKETS.includes(m as any))
                  .map((m) => (
                    <Badge key={m} variant="default" className="cursor-pointer font-mono" onClick={() => toggleMarket(m)}>
                      {m} ×
                    </Badge>
                  ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t.playbook.strategyStep.sessionsLabel}</Label>
            <p className="text-xs text-muted-foreground">{t.playbook.strategyStep.sessionsHint}</p>
            <div className="flex flex-wrap gap-1.5">
              {SESSION_PRESETS.map((preset) => (
                <Button
                  key={preset.labelKey}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => update("sessions", [...form.sessions, { ...preset.session }])}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {t.playbook.strategyStep[preset.labelKey]}
                </Button>
              ))}
            </div>

            {form.sessions.map((session, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end rounded-md border border-border p-2">
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.strategyStep.sessionName}</Label>
                  <Input
                    value={session.name}
                    className="h-8"
                    onChange={(e) => {
                      const next = [...form.sessions];
                      next[i] = { ...next[i], name: e.target.value };
                      update("sessions", next);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.strategyStep.sessionStart}</Label>
                  <TimePicker
                    value={session.startTime}
                    hourAriaLabel={t.playbook.strategyStep.sessionHours}
                    minuteAriaLabel={t.playbook.strategyStep.sessionMinutes}
                    onChange={(startTime) => {
                      const next = [...form.sessions];
                      next[i] = { ...next[i], startTime };
                      update("sessions", next);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.strategyStep.sessionEnd}</Label>
                  <TimePicker
                    value={session.endTime}
                    hourAriaLabel={t.playbook.strategyStep.sessionHours}
                    minuteAriaLabel={t.playbook.strategyStep.sessionMinutes}
                    onChange={(endTime) => {
                      const next = [...form.sessions];
                      next[i] = { ...next[i], endTime };
                      update("sessions", next);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.strategyStep.sessionTimezone}</Label>
                  <Input
                    value={session.timezone}
                    className="h-8 text-xs"
                    onChange={(e) => {
                      const next = [...form.sessions];
                      next[i] = { ...next[i], timezone: e.target.value };
                      update("sessions", next);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t.playbook.strategyStep.removeSession}
                  onClick={() => update("sessions", form.sessions.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">{t.playbook.setupsStep.description}</p>

          <div className="flex flex-col gap-3">
            <Label>{t.playbook.setupsStep.setupsLabel}</Label>
            {form.setups.length === 0 && (
              <p className="text-sm text-muted-foreground">{t.playbook.setupsStep.noSetups}</p>
            )}
            {form.setups.map((setup, i) => (
              <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
                <div className="flex gap-2 items-end">
                  <div className="flex-1 flex flex-col gap-1">
                    <Label className="text-xs">{t.playbook.setupsStep.setupName}</Label>
                    <Input
                      value={setup.name}
                      placeholder={t.playbook.setupsStep.setupNamePlaceholder}
                      className="h-9"
                      onChange={(e) => {
                        const next = [...form.setups];
                        next[i] = { ...next[i], name: e.target.value };
                        update("setups", next);
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t.playbook.setupsStep.removeSetup}
                    onClick={() => update("setups", form.setups.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.setupsStep.entryRules}</Label>
                  <Textarea
                    value={setup.entryRules}
                    placeholder={t.playbook.setupsStep.entryRulesPlaceholder}
                    rows={2}
                    onChange={(e) => {
                      const next = [...form.setups];
                      next[i] = { ...next[i], entryRules: e.target.value };
                      update("setups", next);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">{t.playbook.setupsStep.exitRules}</Label>
                  <Textarea
                    value={setup.exitRules}
                    placeholder={t.playbook.setupsStep.exitRulesPlaceholder}
                    rows={2}
                    onChange={(e) => {
                      const next = [...form.setups];
                      next[i] = { ...next[i], exitRules: e.target.value };
                      update("setups", next);
                    }}
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => update("setups", [...form.setups, { name: "", entryRules: "", exitRules: "" }])}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t.playbook.setupsStep.addSetup}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t.playbook.setupsStep.riskLabel}</Label>
              <div className="flex gap-1.5">
                <select
                  className="h-9 rounded-md border border-input bg-background px-2 text-xs"
                  value={form.maxRiskPerTrade?.type ?? "percent"}
                  onChange={(e) =>
                    update("maxRiskPerTrade", {
                      type: e.target.value as "percent" | "amount",
                      value: form.maxRiskPerTrade?.value ?? 1,
                    })
                  }
                >
                  <option value="percent">{t.playbook.setupsStep.riskTypePercent}</option>
                  <option value="amount">{t.playbook.setupsStep.riskTypeAmount}</option>
                </select>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  className="h-9 font-mono"
                  value={form.maxRiskPerTrade?.value ?? ""}
                  onChange={(e) =>
                    update("maxRiskPerTrade", {
                      type: form.maxRiskPerTrade?.type ?? "percent",
                      value: Number(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t.playbook.setupsStep.maxTradesPerDayLabel}</Label>
              <Input
                type="number"
                min="1"
                className="h-9 font-mono"
                placeholder={t.playbook.setupsStep.maxTradesPerDayPlaceholder}
                value={form.maxTradesPerDay ?? ""}
                onChange={(e) => update("maxTradesPerDay", e.target.value ? Number(e.target.value) : null)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t.playbook.setupsStep.minRiskRewardLabel}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                className="h-9 font-mono"
                placeholder={t.playbook.setupsStep.minRiskRewardPlaceholder}
                value={form.minRiskReward ?? ""}
                onChange={(e) => update("minRiskReward", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">{t.playbook.rulesStep.description}</p>

          <div className="flex flex-col gap-2">
            <Label htmlFor="playbook-account">{t.playbook.referenceAccountLabel}</Label>
            <select
              id="playbook-account"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.sourceAccountId ?? ""}
              onChange={(e) => update("sourceAccountId", e.target.value || null)}
            >
              <option value="">{t.playbook.rulesStep.sourceAccountNone}</option>
              {accounts.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t.playbook.referenceAccountHelp}</p>
          </div>

          <TargetSelector
            accounts={accounts.map((a: any) => ({ id: a.id, name: a.name }))}
            groups={groups.map((g: any) => ({ id: g.id, name: g.name }))}
            selectedAccountIds={form.targetAccountIds}
            selectedGroupIds={form.targetGroupIds}
            onChangeAccounts={(ids) => update("targetAccountIds", ids)}
            onChangeGroups={(ids) => update("targetGroupIds", ids)}
          />

          {selectedAccount && (
            <div className="flex flex-col gap-3 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{t.playbook.rulesStep.derivedTitle}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.playbook.rulesStep.derivedHint}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={hasOverrides}
                    onCheckedChange={(checked) =>
                      update(
                        "propFirmRuleOverrides",
                        checked ? { ...(derivedRules?.derived ?? {}) } : null
                      )
                    }
                    aria-label={t.playbook.rulesStep.overrideToggle}
                  />
                  <span className="text-xs">{t.playbook.rulesStep.overrideToggle}</span>
                </div>
              </div>

              {!derivedRules?.hasPropFirmRules && !hasOverrides ? (
                <p className="text-sm text-muted-foreground">{t.playbook.rulesStep.noRulesDetected}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(
                    [
                      ["maxDailyDrawdown", t.playbook.rulesStep.maxDailyDrawdown],
                      ["maxTotalDrawdown", t.playbook.rulesStep.maxTotalDrawdown],
                      ["profitTarget", t.playbook.rulesStep.profitTarget],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key} className="flex flex-col gap-1.5">
                      <Label className="text-xs">{label}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        className="h-9 font-mono"
                        disabled={!hasOverrides}
                        value={effectiveRules?.[key] ?? ""}
                        onChange={(e) =>
                          update("propFirmRuleOverrides", {
                            ...(form.propFirmRuleOverrides ?? {}),
                            [key]: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {hasOverrides && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => update("propFirmRuleOverrides", null)}
                >
                  {t.playbook.rulesStep.resetOverride}
                </Button>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="playbook-personal-rules">{t.playbook.rulesStep.personalRulesLabel}</Label>
            <Textarea
              id="playbook-personal-rules"
              rows={4}
              value={form.personalRules}
              placeholder={t.playbook.rulesStep.personalRulesPlaceholder}
              onChange={(e) => update("personalRules", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t.playbook.rulesStep.personalRulesHint}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={goBack} disabled={isSaving || (step === 1 && !onCancel)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t.playbook.back}
        </Button>

        {step < PLAYBOOK_STEP_COUNT ? (
          <Button type="button" onClick={goNext}>
            {t.playbook.next}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                {t.playbook.saving}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                {t.playbook.save}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
